import { RefObject, useEffect, useState } from 'react';

/**
 * Receives an element ref object and uses IntersectionObserver to watch
 * until the element is visible, then sets the returned value and stops watching.
 * @param elementRef from useRef() object attached to a DIV element.
 * @param threshold from IntersectionObserver options.
 * @param delayTimeInMilliseconds delay before triggering the visible state.
 * @return boolean
 */
const useOnVisible = (
  elementRef: RefObject<HTMLElement>,
  threshold = 0.5,
  delayTimeInMilliseconds?: number
): boolean => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    const options = {
      rootMargin: '0px',
      threshold,
    };
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          observer.disconnect();

          if (delayTimeInMilliseconds) {
            timeout = setTimeout(() => setIsVisible(true), delayTimeInMilliseconds);
          } else {
            setIsVisible(true);
          }
        }
      });
    }, options);

    observer.observe(elementRef?.current as Element);

    return () => {
      observer.disconnect();

      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [elementRef]);

  return isVisible;
};

export default useOnVisible;
